import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { formatINR } from '../utils/format';
import './productCard.css';

// Path of a product page: /products/:id/:name
export const productPath = product =>
  `/products/${product.id}/${encodeURIComponent(product.name || 'project')}`;

const isImage = media =>
  String(media?.type || '').toLowerCase() === 'image' ||
  String(media?.mimeType || '')
    .toLowerCase()
    .startsWith('image/');

// The product's image media, in order, skipping entries without a URL.
export const productImages = product =>
  (product?.media || []).filter(m => m?.url && isImage(m));

// Blogger/Google-hosted images can be requested at a given width instead of the original upload.
export const sizedImageUrl = (url, width = 800) =>
  url && /googleusercontent\.com/.test(url)
    ? url.replace(/\/s\d+\//, `/w${width}/`)
    : url;

const Cover = ({ product }) => {
  const src = sizedImageUrl(productImages(product)[0]?.url, 800);
  const [failedSrc, setFailedSrc] = useState(null);

  if (!src || failedSrc === src) {
    return (
      <span className="pcard__img pcard__img--empty" aria-hidden="true">
        {(product.name || '?').trim().charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      className="pcard__img"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailedSrc(src)}
    />
  );
};

/** Placeholder with the card's shape, for loading grids. */
export const ProductCardSkeleton = () => (
  <span className="pcard pcard--skeleton" aria-hidden="true">
    <span className="pcard__media pk-skel" />
    <span className="pcard__body">
      <span className="pk-skel pcard__skel-line" />
    </span>
    <span className="pk-skel pcard__skel-line pcard__skel-line--short" />
  </span>
);

/**
 * Image-first project card for catalog grids. The whole card is one link to the product page.
 * Render it inside a list item: <li><ProductCard product={p} /></li>
 */
const ProductCard = ({ product }) => {
  if (!product?.id) return null;

  const reviewCount = Number(product.reviewCount) || 0;
  const rating = Number(product.averageRating) || 0;

  return (
    <Link className="pcard" to={productPath(product)}>
      <span className="pcard__media">
        <Cover product={product} />
        {product.isFeatured && (
          <span className="pcard__badge">Featured</span>
        )}
      </span>
      <span className="pcard__body">
        <span className="pcard__name">{product.name || 'Untitled project'}</span>
        <span className="pcard__price">
          {formatINR(product.price, { free: true })}
        </span>
      </span>
      {(product.category || reviewCount > 0) && (
        <span className="pcard__meta">
          {product.category && (
            <span className="pcard__cat">{product.category}</span>
          )}
          {reviewCount > 0 && rating > 0 && (
            <span className="pcard__rating">
              <Star aria-hidden="true" />
              <span aria-hidden="true">
                {rating.toFixed(1)} ({reviewCount})
              </span>
              <span className="sr-only">
                {`Rated ${rating.toFixed(1)} out of 5 from ${reviewCount} ${
                  reviewCount === 1 ? 'review' : 'reviews'
                }`}
              </span>
            </span>
          )}
        </span>
      )}
    </Link>
  );
};

export default ProductCard;
